import { useEffect, useMemo, useRef, useState } from 'react';
import { X, Send, AlertTriangle, CheckCircle2, Eye, EyeOff, Users } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { AdminUserEntry } from '../../lib/adminUserView';
import {
  composeEmail,
  DEFAULT_POSTAL_ADDRESS,
  imageUrl,
  parseBlocks,
  type EmailFormat,
} from '../../lib/emailTemplate';
import { NEWSLETTER_ISSUES } from '../../newsletter/issues';
import {
  MAILING_LIST_KEYS,
  MAILING_LIST_BLURBS,
  MAILING_LIST_LABELS,
  LIST_CARRIES_POSTAL_ADDRESS,
  summarizeSelection,
  type MailingList,
} from '../../lib/bulkEmail';

interface BatchResult {
  user_id: string;
  email: string | null;
  status: 'sent' | 'skipped' | 'failed';
  reason: string | null;
}

interface Batch {
  id: string;
  list: MailingList;
  format: EmailFormat;
  subject: string;
  status: 'running' | 'done' | 'failed';
  recipient_count: number;
  sent_count: number;
  skipped_count: number;
  failed_count: number;
  results: BatchResult[];
  error: string | null;
  finished_at: string | null;
}

interface AdminBulkEmailModalProps {
  /** Every user the table knows about, so skip reasons can name people. */
  users: AdminUserEntry[];
  selectedIds: string[];
  /**
   * didSend is true once a batch has been handed to the sender. The Users table
   * clears the ticked boxes on that, so a closed window cannot be reopened and
   * sent a second time by reflex.
   */
  onClose: (didSend: boolean) => void;
}

const POLL_MS = 2000;

export default function AdminBulkEmailModal({ users, selectedIds, onClose }: AdminBulkEmailModalProps) {
  const [list, setList] = useState<MailingList>('newsletter');
  // The newsletter is the case that wants headings and a chart; a note about
  // somebody's own job reads better as the plain text it has always been.
  const [format, setFormat] = useState<EmailFormat>('html');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [issueNumber, setIssueNumber] = useState<number | null>(null);
  const [imageStatus, setImageStatus] = useState<Record<string, 'checking' | 'ok' | 'broken'>>({});
  const [showPreview, setShowPreview] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [batch, setBatch] = useState<Batch | null>(null);
  const pollRef = useRef<number | null>(null);

  const { sending, skipping } = useMemo(
    () => summarizeSelection(users, selectedIds, list),
    [users, selectedIds, list]
  );

  const getJwt = async () => {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token ?? '';
  };

  // Polling is how the screen learns anything at all: the sender is a Netlify
  // background function, which answers 202 and then talks only to its
  // email_batches row.
  useEffect(() => {
    if (!batch || batch.status !== 'running') return;

    const tick = async () => {
      try {
        const jwt = await getJwt();
        const resp = await fetch(
          `/.netlify/functions/admin-send-bulk?batchId=${encodeURIComponent(batch.id)}`,
          { headers: { Authorization: `Bearer ${jwt}` } }
        );
        const data = await resp.json();
        if (resp.ok && data.batch) setBatch(data.batch as Batch);
      } catch {
        // A dropped poll is not a failed send. Leave the last known state on
        // screen and try again on the next tick.
      }
    };

    pollRef.current = window.setInterval(tick, POLL_MS);
    return () => {
      if (pollRef.current !== null) window.clearInterval(pollRef.current);
    };
  }, [batch]);

  const start = async () => {
    setStarting(true);
    setError(null);
    try {
      const jwt = await getJwt();
      const resp = await fetch('/.netlify/functions/admin-send-bulk', {
        method: 'POST',
        headers: { Authorization: `Bearer ${jwt}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          list,
          subject,
          message,
          // Send the whole selection. The server decides who is mailable at
          // send time, and records anyone it left out — the split shown above
          // is a preview of that decision, not a substitute for it.
          format,
          userIds: selectedIds,
        }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Failed to start the send');

      setBatch({
        id: data.batchId,
        list,
        format,
        subject,
        status: 'running',
        recipient_count: selectedIds.length,
        sent_count: 0,
        skipped_count: 0,
        failed_count: 0,
        results: [],
        error: null,
        finished_at: null,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start the send');
    } finally {
      setStarting(false);
      setConfirming(false);
    }
  };

  const chooseIssue = (n: number | null) => {
    setIssueNumber(n);
    const issue = NEWSLETTER_ISSUES.find(i => i.number === n);
    setSubject(issue?.subject ?? '');
    setMessage(issue?.body ?? '');
    if (issue) setFormat('html');
  };

  // Every image the message places, as the reader's mail client will fetch it.
  const imageUrls = useMemo(
    () =>
      format === 'html'
        ? parseBlocks(message).flatMap(b =>
            b.kind === 'image' ? [imageUrl(b.src, window.location.origin)] : []
          )
        : [],
    [message, format]
  );

  // Load each image before allowing a send. A picture that is not deployed yet
  // reaches every inbox as a broken box, and mail clients remember the failure
  // even after the file appears.
  useEffect(() => {
    let cancelled = false;
    setImageStatus(Object.fromEntries(imageUrls.map(u => [u, 'checking' as const])));
    for (const url of imageUrls) {
      const img = new Image();
      img.onload = () => !cancelled && setImageStatus(s => ({ ...s, [url]: 'ok' }));
      img.onerror = () => !cancelled && setImageStatus(s => ({ ...s, [url]: 'broken' }));
      img.src = url;
    }
    return () => {
      cancelled = true;
    };
  }, [imageUrls]);

  const brokenImages = imageUrls.filter(u => imageStatus[u] === 'broken');
  const imagesReady = imageUrls.every(u => imageStatus[u] === 'ok');

  const canSend =
    !starting &&
    !batch &&
    sending.length > 0 &&
    subject.trim() !== '' &&
    message.trim() !== '' &&
    imagesReady;

  const previewFor = sending[0] ?? null;

  const preview = useMemo(
    () =>
      composeEmail(message, {
        format,
        fullName: previewFor?.full_name ?? null,
        // Stand-ins for the real per-recipient values, which only exist at send
        // time. Everything else here is exactly what the sender will produce.
        optOutUrl: `${window.location.origin}/unsubscribe?t=example&l=${list}`,
        postalAddress: LIST_CARRIES_POSTAL_ADDRESS[list] ? DEFAULT_POSTAL_ADDRESS : '',
        siteBase: window.location.origin,
      }),
    [message, format, previewFor, list]
  );

  const close = () => onClose(batch !== null);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-gray-900">
              {batch ? 'Sending' : `Email ${selectedIds.length} selected`}
            </h3>
          </div>
          <button onClick={close} className="text-gray-400 hover:text-gray-600" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto space-y-5">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {batch ? (
            <BatchProgress batch={batch} users={users} />
          ) : (
            <>
              <section>
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-2">
                  Which list
                </p>
                <div className="space-y-2">
                  {MAILING_LIST_KEYS.map(key => (
                    <label
                      key={key}
                      className={`flex gap-3 p-3 border rounded-md cursor-pointer ${
                        list === key ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="mailing-list"
                        className="mt-1"
                        checked={list === key}
                        onChange={() => setList(key)}
                      />
                      <span>
                        <span className="block text-sm font-medium text-gray-900">
                          {MAILING_LIST_LABELS[key]}
                        </span>
                        <span className="block text-xs text-gray-500 mt-0.5">
                          {MAILING_LIST_BLURBS[key]}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              </section>

              {list === 'newsletter' && (
                <section>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-2">
                    Issue
                  </p>
                  <select
                    value={issueNumber ?? ''}
                    onChange={e => chooseIssue(e.target.value ? Number(e.target.value) : null)}
                    data-testid="newsletter-issue-select"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Write my own</option>
                    {[...NEWSLETTER_ISSUES].reverse().map(i => (
                      <option key={i.number} value={i.number}>
                        Issue {i.number}: {i.feature}
                      </option>
                    ))}
                  </select>
                </section>
              )}

              <section>
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-2">
                  Format
                </p>
                <div className="flex gap-2">
                  {(['html', 'text'] as EmailFormat[]).map(f => (
                    <button
                      key={f}
                      type="button"
                      onClick={() => setFormat(f)}
                      className={`px-3 py-1.5 text-sm rounded-md border ${
                        format === f
                          ? 'border-blue-500 bg-blue-50 text-blue-800 font-medium'
                          : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {f === 'html' ? 'Formatted (with images)' : 'Plain text'}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {format === 'html'
                    ? 'Wrapped in the TrueLoad template. A plain-text version goes out alongside it, so a reader whose images are blocked still gets the whole message.'
                    : 'Sent exactly as typed. Headings and images are left as literal text.'}
                </p>
              </section>

              <RecipientSummary sending={sending.length} skipping={skipping} />

              <section className="space-y-3">
                <label className="block">
                  <span className="block text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">
                    Subject
                  </span>
                  <input
                    type="text"
                    value={subject}
                    onChange={e => setSubject(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </label>

                <label className="block">
                  <span className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                      Message
                    </span>
                    <span className="text-xs text-gray-400">
                      <code className="bg-gray-100 px-1 rounded">{'{{first_name}}'}</code> becomes
                      each reader’s first name
                    </span>
                  </span>
                  {format === 'html' && (
                    <span className="block text-xs text-gray-500 mb-1">
                      Blank lines separate paragraphs.{' '}
                      <code className="bg-gray-100 px-1 rounded">## Heading</code> on its own line
                      makes a heading.{' '}
                      <code className="bg-gray-100 px-1 rounded">
                        {'[[image: chart.png | what it shows]]'}
                      </code>{' '}
                      on its own line places an image from{' '}
                      <code className="bg-gray-100 px-1 rounded">public/newsletter/</code>. Write the
                      alt text as if it were the picture — for many readers it is.
                    </span>
                  )}
                  <textarea
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    rows={12}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </label>

                <button
                  type="button"
                  onClick={() => setShowPreview(v => !v)}
                  className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800"
                >
                  {showPreview ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  {showPreview ? 'Hide preview' : 'Preview as the first recipient'}
                </button>

                {brokenImages.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">
                    <p className="flex items-center gap-1.5 font-medium">
                      <AlertTriangle className="w-4 h-4" />
                      {brokenImages.length === 1 ? 'An image does not load' : 'Images do not load'}, so
                      sending is off until {brokenImages.length === 1 ? 'it is' : 'they are'} live:
                    </p>
                    <ul className="mt-1 text-xs break-all">
                      {brokenImages.map(u => (
                        <li key={u}>{u}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {showPreview && (
                  <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                    <p className="text-xs text-gray-500 mb-2">
                      As {previewFor?.email || 'the first recipient'} will read it, rendered by the
                      same code that sends. The unsubscribe link is a stand-in; each reader gets
                      their own.
                    </p>
                    {preview.html ? (
                      // Sandboxed: the preview is a whole document with its own
                      // styles, and it must not be able to touch the admin page.
                      <iframe
                        title="Email preview"
                        sandbox=""
                        srcDoc={preview.html}
                        className="w-full h-96 bg-white border border-gray-200 rounded"
                      />
                    ) : (
                      <pre className="text-xs text-gray-800 whitespace-pre-wrap font-mono">
                        {preview.text}
                      </pre>
                    )}
                  </div>
                )}
              </section>
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between gap-3">
          {batch ? (
            <>
              <p className="text-xs text-gray-500">
                {batch.status === 'running'
                  ? 'You can close this — the send carries on without the window open.'
                  : 'Finished.'}
              </p>
              <button
                onClick={close}
                className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Close
              </button>
            </>
          ) : confirming ? (
            <>
              <p className="text-sm text-gray-700">
                Send to {sending.length} {sending.length === 1 ? 'person' : 'people'}? This cannot be
                undone.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setConfirming(false)}
                  className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  Back
                </button>
                <button
                  onClick={start}
                  disabled={starting}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 rounded-md"
                >
                  <Send className="w-4 h-4" />
                  {starting ? 'Starting…' : `Send ${sending.length}`}
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-xs text-gray-500">
                One message per person — never a shared To or BCC line.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={close}
                  className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setConfirming(true)}
                  disabled={!canSend}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 rounded-md"
                >
                  <Send className="w-4 h-4" />
                  Review and send
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function RecipientSummary({
  sending,
  skipping,
}: {
  sending: number;
  skipping: { user: AdminUserEntry; reason: string }[];
}) {
  return (
    <section className="bg-gray-50 border border-gray-200 rounded-md p-3">
      <p className="text-sm text-gray-900">
        <span className="font-semibold">{sending}</span> will receive this.
        {skipping.length > 0 && (
          <span className="text-gray-600"> {skipping.length} selected will not:</span>
        )}
      </p>
      {skipping.length > 0 && (
        <ul className="mt-2 space-y-1">
          {skipping.map(({ user, reason }) => (
            <li key={user.id} className="text-xs text-gray-600 flex gap-2">
              <span className="truncate max-w-[16rem]">{user.email || user.id}</span>
              <span className="text-gray-400">— {reason}</span>
            </li>
          ))}
        </ul>
      )}
      {sending === 0 && (
        <p className="mt-2 text-xs text-amber-700 flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5" />
          Nobody in this selection can receive this list.
        </p>
      )}
    </section>
  );
}

function BatchProgress({ batch, users }: { batch: Batch; users: AdminUserEntry[] }) {
  const nameFor = (result: BatchResult) => {
    const user = users.find(u => u.id === result.user_id);
    return result.email || user?.email || result.user_id;
  };

  const done = batch.sent_count + batch.skipped_count + batch.failed_count;
  const pct = batch.recipient_count ? Math.round((done / batch.recipient_count) * 100) : 0;

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center justify-between text-sm mb-1">
          <span className="text-gray-700">
            {batch.status === 'running'
              ? `${done} of ${batch.recipient_count}`
              : `Finished — ${batch.sent_count} sent`}
          </span>
          {batch.status === 'done' && <CheckCircle2 className="w-4 h-4 text-green-600" />}
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full ${batch.status === 'failed' ? 'bg-red-500' : 'bg-blue-600'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {batch.error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">
          {batch.error}
        </div>
      )}

      <div className="flex gap-4 text-sm">
        <span className="text-green-700">{batch.sent_count} sent</span>
        <span className="text-gray-500">{batch.skipped_count} skipped</span>
        <span className={batch.failed_count ? 'text-red-700' : 'text-gray-400'}>
          {batch.failed_count} failed
        </span>
      </div>

      {batch.results.length > 0 && (
        <div className="border border-gray-200 rounded-md divide-y divide-gray-100 max-h-64 overflow-y-auto">
          {batch.results.map(r => (
            <div key={r.user_id} className="px-3 py-2 flex items-center justify-between gap-3 text-xs">
              <span className="truncate text-gray-700">{nameFor(r)}</span>
              <span
                className={
                  r.status === 'sent'
                    ? 'text-green-700 whitespace-nowrap'
                    : r.status === 'failed'
                      ? 'text-red-700 whitespace-nowrap'
                      : 'text-gray-500 whitespace-nowrap'
                }
              >
                {r.status === 'sent' ? 'Sent' : `${r.status === 'failed' ? 'Failed' : 'Skipped'} — ${r.reason}`}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
